import { useState, type FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { channels } from "@/mocks/news";

interface SiteHeaderProps {
  brandAsHeading?: boolean;
}

const todayText = "2026 年 9 月 10 日 · 星期四";

export default function SiteHeader({ brandAsHeading = false }: SiteHeaderProps) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const key = keyword.trim();
    if (!key) {
      navigate("/channel/all");
      return;
    }
    navigate(`/channel/all?q=${encodeURIComponent(key)}`);
    setMenuOpen(false);
  };

  const brand = (
    <Link to="/" className="flex items-center gap-2.5" aria-label="爆料官网 首页">
      <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50 dark:text-foreground-950">
        <i className="ri-flashlight-fill text-lg" aria-hidden="true"></i>
      </span>
      <span className="flex flex-col leading-none">
        <span className="font-heading text-xl font-bold tracking-wide text-foreground-950">
          爆料官网
        </span>
        <span className="mt-1 font-label text-[10px] tracking-[0.3em] text-secondary-600">
          BAOLIAO · TODAY
        </span>
      </span>
    </Link>
  );

  return (
    <>
      <div className="hidden w-full items-center justify-between border-b border-background-200 bg-background-100 px-4 py-2 font-label text-xs text-secondary-700 md:flex md:px-6">
        <p className="flex items-center gap-2">
          <i className="ri-calendar-line text-sm" aria-hidden="true"></i>
          <span>{todayText}</span>
          <span className="text-secondary-300">|</span>
          <span>今日发布频道 · 每日 08:00 更新</span>
        </p>
        <p className="flex items-center gap-4">
          <span className="flex items-center gap-1.5">
            <i className="ri-radar-line text-sm text-primary-500" aria-hidden="true"></i>
            全网线索实时汇总
          </span>
          <Link to="/about" className="transition-colors hover:text-primary-600">
            关于我们
          </Link>
        </p>
      </div>

      <header className="sticky top-0 z-40 w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
        <div className="flex w-full items-center justify-between gap-4 px-4 py-3 md:px-6">
          <div className="flex items-center gap-6">
            {brandAsHeading ? <h1 className="m-0">{brand}</h1> : brand}
            <nav className="hidden items-center gap-5 lg:flex" aria-label="主导航">
              <NavLink
                to="/"
                className={({ isActive }) =>
                  `font-label text-sm transition-colors ${
                    isActive ? "text-primary-600" : "text-foreground-700 hover:text-primary-600"
                  }`
                }
              >
                首页
              </NavLink>
              <NavLink
                to="/channel/all"
                className={({ isActive }) =>
                  `font-label text-sm transition-colors ${
                    isActive ? "text-primary-600" : "text-foreground-700 hover:text-primary-600"
                  }`
                }
              >
                全部爆料
              </NavLink>
              {channels.slice(0, 3).map((channel) => (
                <Link
                  key={channel.slug}
                  to={`/channel/${channel.slug}`}
                  className="font-label text-sm text-foreground-700 transition-colors hover:text-primary-600"
                >
                  {channel.name}
                </Link>
              ))}
            </nav>
          </div>

          <div className="flex items-center gap-3">
            <form
              onSubmit={handleSearch}
              role="search"
              className="hidden items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-1.5 md:flex"
            >
              <i className="ri-search-line text-base text-secondary-600" aria-hidden="true"></i>
              <input
                type="search"
                name="q"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索爆料、明星或话题"
                aria-label="站内搜索"
                className="w-40 bg-transparent font-label text-sm text-foreground-800 outline-none placeholder:text-secondary-500 focus:w-52 xl:w-56 xl:focus:w-64"
              />
              <button
                type="submit"
                className="whitespace-nowrap rounded-full bg-primary-500 px-3 py-1 font-label text-xs text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
              >
                搜索
              </button>
            </form>
            <Link
              to="/about"
              className="hidden whitespace-nowrap rounded-md bg-foreground-950 px-4 py-2 font-label text-xs text-background-50 transition-colors hover:bg-foreground-800 md:block"
            >
              我要爆料
            </Link>
            <button
              type="button"
              onClick={() => setMenuOpen((prev) => !prev)}
              aria-label="打开导航菜单"
              aria-expanded={menuOpen}
              className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-800 lg:hidden"
            >
              <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-2-line text-xl"} aria-hidden="true"></i>
            </button>
          </div>
        </div>

        <div className="hidden w-full items-center gap-1 overflow-x-auto border-t border-background-200 px-4 py-2 md:flex md:px-6">
          <span className="mr-2 flex items-center gap-1 whitespace-nowrap font-label text-xs font-medium text-primary-600">
            <i className="ri-arrow-right-s-fill" aria-hidden="true"></i>
            今日聚焦
          </span>
          {channels.map((channel) => (
            <Link
              key={channel.slug}
              to={`/channel/${channel.slug}`}
              className="whitespace-nowrap rounded-full px-3 py-1 font-label text-xs text-secondary-800 transition-colors hover:bg-secondary-100 hover:text-secondary-950"
            >
              # {channel.name}
            </Link>
          ))}
        </div>

        {menuOpen && (
          <div className="border-t border-background-200 bg-background-50 px-4 pb-5 pt-4 lg:hidden">
            <form
              onSubmit={handleSearch}
              role="search"
              className="mb-4 flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-2"
            >
              <i className="ri-search-line text-base text-secondary-600" aria-hidden="true"></i>
              <input
                type="search"
                name="q"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索爆料、明星或话题"
                aria-label="站内搜索"
                className="w-full bg-transparent font-label text-sm text-foreground-800 outline-none placeholder:text-secondary-500"
              />
            </form>
            <nav className="flex flex-col" aria-label="移动端导航">
              <Link
                to="/"
                onClick={() => setMenuOpen(false)}
                className="border-b border-background-200 py-3 font-label text-sm text-foreground-800"
              >
                首页
              </Link>
              <Link
                to="/channel/all"
                onClick={() => setMenuOpen(false)}
                className="border-b border-background-200 py-3 font-label text-sm text-foreground-800"
              >
                全部爆料
              </Link>
              {channels.map((channel) => (
                <Link
                  key={channel.slug}
                  to={`/channel/${channel.slug}`}
                  onClick={() => setMenuOpen(false)}
                  className="border-b border-background-200 py-3 font-label text-sm text-foreground-800"
                >
                  {channel.name}
                </Link>
              ))}
              <Link
                to="/about"
                onClick={() => setMenuOpen(false)}
                className="py-3 font-label text-sm text-foreground-800"
              >
                关于我们 · 我要爆料
              </Link>
            </nav>
          </div>
        )}
      </header>
    </>
  );
}